import React, { useState, useEffect } from 'react';
import {
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Switch,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  InputAdornment,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormGroup,
  Divider,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  ContentCopy as ContentCopyIcon,
  Add as AddIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { api } from '../services/authService';
import theme from '../theme/theme';

const CampaignOperatingHours = ({ campaignId, is24_7: initialIs24_7, on24_7Change }) => {
  const [operatingHours, setOperatingHours] = useState([]);
  const [is24_7, setIs24_7] = useState(initialIs24_7);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [sourceDay, setSourceDay] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);
  const [timeSlotErrors, setTimeSlotErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (campaignId) {
      fetchOperatingHours();
    }
  }, [campaignId]);

  // Fetch operating hours
  const fetchOperatingHours = async () => {
    try {
      const response = await api.get(`/crm/campaigns/${campaignId}/operating-hours/`);
      if (response.data && response.data.length > 0) {
        // Define the order of days
        const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        
        // Transform and sort the data
        const transformedData = response.data
          .map(day => ({
            id: day.id,
            day_of_week: day.day_of_week,
            timeSlots: day.is_closed ? [{
              id: null,
              start_time: '',
              end_time: '',
              is_closed: true
            }] : day.time_slots.map(slot => ({
              id: slot.id,
              start_time: slot.start_time,
              end_time: slot.end_time,
              is_closed: false
            }))
          }))
          .sort((a, b) => dayOrder.indexOf(a.day_of_week) - dayOrder.indexOf(b.day_of_week));
          
        setOperatingHours(transformedData);
      } else {
        // Initialize with empty operating hours for each day in the correct order
        const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        const emptyHours = dayOrder.map(day => ({
          id: null,
          day_of_week: day,
          timeSlots: [{
            id: null,
            start_time: '',
            end_time: '',
            is_closed: true
          }]
        }));
        setOperatingHours(emptyHours);
      }
    } catch (error) {
      console.error('Error fetching operating hours:', error);
      showSnackbar('Error fetching operating hours', 'error');
    }
  };

  // Handle 24/7 toggle
  const handle24_7Toggle = async (event) => {
    const newValue = event.target.checked;
    setIs24_7(newValue);
    try {
      await api.patch(`/crm/campaigns/${campaignId}/`, { is_24_7: newValue });
      if (on24_7Change) {
        on24_7Change(newValue);
      }
    } catch (error) {
      console.error('Error updating 24/7 setting:', error);
      setIs24_7(!newValue); // Revert on error
    }
  };

  // Check for time slot overlaps
  const checkTimeSlotOverlaps = (day, slotIndex, newStartTime, newEndTime) => {
    const dayHours = operatingHours.find(h => h.day_of_week === day);
    if (!dayHours || !newStartTime || !newEndTime) return false;

    const newStart = new Date(`2000-01-01T${newStartTime}`);
    const newEnd = new Date(`2000-01-01T${newEndTime}`);

    // Check if end time is before start time
    if (newEnd <= newStart) {
      return true;
    }

    // Check overlap with all other slots
    return dayHours.timeSlots.some((slot, index) => {
      // Skip the current slot being edited
      if (index === slotIndex || !slot.start_time || !slot.end_time) return false;
      
      const slotStart = new Date(`2000-01-01T${slot.start_time}`);
      const slotEnd = new Date(`2000-01-01T${slot.end_time}`);
      
      // Check for any overlap
      return (newStart < slotEnd && newEnd > slotStart);
    });
  };

  // Handle operating hours change with overlap validation
  const handleOperatingHoursChange = (day, slotIndex, field, value) => {
    const dayHours = operatingHours.find(h => h.day_of_week === day);
    if (!dayHours) return;

    const updatedTimeSlots = [...dayHours.timeSlots];
    const currentSlot = updatedTimeSlots[slotIndex];
    const newSlot = { ...currentSlot, [field]: value };

    // Check for overlaps only when both times are set
    if (field === 'start_time' && newSlot.end_time) {
      const hasOverlap = checkTimeSlotOverlaps(day, slotIndex, value, newSlot.end_time);
      setTimeSlotErrors(prev => ({
        ...prev,
        [`${day}-${slotIndex}`]: hasOverlap ? 'Time slot overlaps with another slot' : null
      }));
    } else if (field === 'end_time' && newSlot.start_time) {
      const hasOverlap = checkTimeSlotOverlaps(day, slotIndex, newSlot.start_time, value);
      setTimeSlotErrors(prev => ({
        ...prev,
        [`${day}-${slotIndex}`]: hasOverlap ? 'Time slot overlaps with another slot' : null
      }));
    }

    // Update the time slot
    updatedTimeSlots[slotIndex] = newSlot;
    
    // Update the operating hours state
    setOperatingHours(prev => prev.map(hour => 
      hour.day_of_week === day ? { ...hour, timeSlots: updatedTimeSlots } : hour
    ));
  };

  // Handle time increment/decrement
  const handleTimeChange = (day, slotIndex, field, increment) => {
    const currentTime = operatingHours.find(h => h.day_of_week === day).timeSlots[slotIndex][field];
    if (!currentTime) return;

    const [hours, minutes] = currentTime.split(':').map(Number);
    let newHours = hours;
    let newMinutes = minutes;

    if (increment) {
      newMinutes += 15;
      if (newMinutes >= 60) {
        newMinutes = 0;
        newHours = (newHours + 1) % 24;
      }
    } else {
      newMinutes -= 15;
      if (newMinutes < 0) {
        newMinutes = 45;
        newHours = (newHours - 1 + 24) % 24;
      }
    }

    const newTime = `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
    handleOperatingHoursChange(day, slotIndex, field, newTime);
  };

  // Delete a time slot
  const handleDeleteTimeSlot = async (day, slotIndex) => {
    const dayHours = operatingHours.find(h => h.day_of_week === day);
    if (!dayHours) return;

    const slotToDelete = dayHours.timeSlots[slotIndex];
    if (!slotToDelete) return;

    try {
      // If the slot has an ID and the day has an ID, delete it from the backend
      if (slotToDelete.id && dayHours.id) {
        await api.delete(`/crm/campaigns/${campaignId}/operating-hours/${dayHours.id}/time-slots/${slotToDelete.id}/`);
      }

      // Update the UI
      const updatedTimeSlots = dayHours.timeSlots.filter((_, index) => index !== slotIndex);
      
      // If this was the last time slot, add a new empty one
      if (updatedTimeSlots.length === 0) {
        updatedTimeSlots.push({
          id: null,
          start_time: '',
          end_time: '',
          is_closed: true
        });
      }

      setOperatingHours(prev => prev.map(hour => 
        hour.day_of_week === day 
          ? { ...hour, timeSlots: updatedTimeSlots }
          : hour
      ));

      showSnackbar('Time slot deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting time slot:', error);
      showSnackbar('Error deleting time slot', 'error');
    }
  };

  // Save operating hours
  const handleSaveOperatingHours = async () => {
    try {
      // Transform the data into the required format
      const formattedData = operatingHours.map(day => ({
        id: day.id,
        day_of_week: day.day_of_week,
        is_closed: day.timeSlots[0].is_closed,
        time_slots: day.timeSlots[0].is_closed ? [] : day.timeSlots.map(slot => ({
          id: slot.id,
          start_time: slot.start_time,
          end_time: slot.end_time
        }))
      }));

      // Use the bulk_update action
      await api.post(`/crm/campaigns/${campaignId}/operating-hours/bulk_update/`, formattedData);

      showSnackbar('Operating hours saved successfully', 'success');
    } catch (error) {
      console.error('Error saving operating hours:', error);
      showSnackbar('Error saving operating hours', 'error');
    }
  };

  // Apply quick settings
  const applyQuickSettings = (days) => {
    const defaultHours = { start_time: '09:00', end_time: '17:00', is_closed: false };
    const clearHours = { start_time: '', end_time: '', is_closed: true };
    
    setOperatingHours(prev => prev.map(hour => {
      if (days.includes(hour.day_of_week)) {
        return {
          ...hour,
          timeSlots: [{ ...defaultHours, id: null }]
        };
      } else {
        return {
          ...hour,
          timeSlots: [{ ...clearHours, id: null }]
        };
      }
    }));
  };

  // Clear all settings
  const handleClearAll = () => {
    const defaultState = operatingHours.map(hour => ({
      ...hour,
      timeSlots: [{
        id: null,
        start_time: '',
        end_time: '',
        is_closed: true
      }]
    }));
    setOperatingHours(defaultState);
  };

  // Handle copy settings
  const handleCopySettings = (day) => {
    setSourceDay(day);
    setSelectedDays([]);
    setCopyDialogOpen(true);
  };

  // Handle copy confirmation
  const handleCopyConfirm = () => {
    if (!sourceDay || selectedDays.length === 0) return;

    const sourceHours = operatingHours.find(h => h.day_of_week === sourceDay);
    if (!sourceHours) return;

    setOperatingHours(prev => prev.map(hour => {
      if (selectedDays.includes(hour.day_of_week)) {
        return {
          ...hour,
          timeSlots: sourceHours.timeSlots.map(slot => ({
            ...slot,
            id: null
          }))
        };
      }
      return hour;
    }));

    setCopyDialogOpen(false);
  };

  // Handle day selection for copy
  const handleDaySelect = (day) => {
    setSelectedDays(prev => {
      if (prev.includes(day)) {
        return prev.filter(d => d !== day);
      }
      return [...prev, day];
    });
  };

  // Add new time slot
  const handleAddTimeSlot = (day) => {
    setOperatingHours(prev => prev.map(hour => {
      if (hour.day_of_week === day) {
        return {
          ...hour,
          timeSlots: [...hour.timeSlots, {
            id: null,
            start_time: '',
            end_time: '',
            is_closed: false
          }]
        };
      }
      return hour;
    }));
  };

  // Clear operating hours for a day
  const handleClearDay = async (day) => {
    const dayHours = operatingHours.find(h => h.day_of_week === day);
    if (!dayHours) return;

    try {
      // Delete all time slots for this day from the backend
      const deletePromises = dayHours.timeSlots
        .filter(slot => slot.id) // Only delete slots that exist in the backend
        .map(slot => api.delete(`/crm/campaigns/${campaignId}/operating-hours/${dayHours.id}/time-slots/${slot.id}/`));
      
      await Promise.all(deletePromises);

      // Update the UI
      setOperatingHours(prev => prev.map(hour => 
        hour.day_of_week === day 
          ? { 
              ...hour, 
              timeSlots: [{
                id: null,
                start_time: '',
                end_time: '',
                is_closed: true
              }]
            }
          : hour
      ));

      showSnackbar('Operating hours cleared successfully', 'success');
    } catch (error) {
      console.error('Error clearing operating hours:', error);
      showSnackbar('Error clearing operating hours', 'error');
    }
  };

  // Add a new day to the schedule
  const handleAddDay = async (day) => {
    try {
      // Create a new operating hours record for the day
      const response = await api.post(`/crm/campaigns/${campaignId}/operating-hours/`, {
        day_of_week: day,
        is_closed: true
      });

      // Update the UI with the new day
      setOperatingHours(prev => {
        const newDay = {
          id: response.data.id,
          day_of_week: day,
          timeSlots: [{
            id: null,
            start_time: '',
            end_time: '',
            is_closed: true
          }]
        };

        // Insert the new day in the correct order
        const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        const index = dayOrder.indexOf(day);
        const newHours = [...prev];
        newHours.splice(index, 0, newDay);
        return newHours;
      });

      showSnackbar(`${day.charAt(0).toUpperCase() + day.slice(1)} added to schedule`, 'success');
    } catch (error) {
      console.error('Error adding day:', error);
      showSnackbar('Error adding day to schedule', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                24/7 Operation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enable this option if your campaign operates around the clock
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={is24_7}
                  onChange={handle24_7Toggle}
                  color="primary"
                  size="large"
                />
              }
              label={is24_7 ? "Enabled" : "Disabled"}
            />
          </Box>
        </CardContent>
      </Card>

      {!is24_7 && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Apply predefined schedules to multiple days
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  onClick={() => applyQuickSettings(['monday', 'tuesday', 'wednesday', 'thursday', 'friday'])}
                  startIcon={<AccessTimeIcon />}
                >
                  Weekdays (9 AM - 5 PM)
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => applyQuickSettings(['saturday', 'sunday'])}
                  startIcon={<AccessTimeIcon />}
                >
                  Weekends (9 AM - 5 PM)
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => applyQuickSettings(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'])}
                  startIcon={<AccessTimeIcon />}
                >
                  All Days (9 AM - 5 PM)
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleClearAll}
                  startIcon={<DeleteIcon />}
                >
                  Clear All
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Daily Schedule
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                    const existingDays = operatingHours.map(h => h.day_of_week);
                    const missingDays = allDays.filter(day => !existingDays.includes(day));
                    
                    if (missingDays.length > 0) {
                      missingDays.forEach(day => handleAddDay(day));
                    } else {
                      showSnackbar('All days are already in the schedule', 'info');
                    }
                  }}
                  startIcon={<AddIcon />}
                >
                  Add Missing Days
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Set specific hours for each day of the week
              </Typography>

              <Grid container spacing={3}>
                {operatingHours.map((hour) => (
                  <Grid item xs={12} key={hour.day_of_week}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle1" sx={{ flexGrow: 1, fontWeight: 'medium' }}>
                            {hour.day_of_week.charAt(0).toUpperCase() + hour.day_of_week.slice(1)}
                          </Typography>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={!hour.timeSlots[0].is_closed}
                                onChange={(e) => handleOperatingHoursChange(hour.day_of_week, 0, 'is_closed', !e.target.checked)}
                                color="primary"
                              />
                            }
                            label={hour.timeSlots[0].is_closed ? "Closed" : "Open"}
                          />
                        </Box>

                        {!hour.timeSlots[0].is_closed && (
                          <>
                            {hour.timeSlots.map((slot, slotIndex) => (
                              <Box key={slotIndex} sx={{ mb: 2 }}>
                                {timeSlotErrors[`${hour.day_of_week}-${slotIndex}`] && (
                                  <Alert severity="error" sx={{ mb: 2 }}>
                                    {timeSlotErrors[`${hour.day_of_week}-${slotIndex}`]}
                                  </Alert>
                                )}
                                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AccessTimeIcon />}
                                    onClick={() => {
                                      const dayHours = operatingHours.find(h => h.day_of_week === hour.day_of_week);
                                      if (!dayHours) return;

                                      const updatedTimeSlots = [...dayHours.timeSlots];
                                      updatedTimeSlots[slotIndex] = {
                                        ...updatedTimeSlots[slotIndex],
                                        start_time: '09:00',
                                        end_time: '17:00'
                                      };

                                      setOperatingHours(prev => prev.map(h => 
                                        h.day_of_week === hour.day_of_week 
                                          ? { ...h, timeSlots: updatedTimeSlots }
                                          : h
                                      ));
                                    }}
                                    disabled={slot.start_time === '09:00' && slot.end_time === '17:00'}
                                  >
                                    Set 9 AM - 5 PM
                                  </Button>
                                  <Typography variant="body2" color="text.secondary">
                                    {slot.start_time && slot.end_time 
                                      ? `Current hours: ${slot.start_time} - ${slot.end_time}`
                                      : 'No hours set'}
                                  </Typography>
                                </Box>

                                <Grid container spacing={2} alignItems="center">
                                  <Grid item xs={12} sm={5}>
                                    <TextField
                                      label="Opening Time"
                                      type="time"
                                      value={slot.start_time || ''}
                                      onChange={(e) => handleOperatingHoursChange(hour.day_of_week, slotIndex, 'start_time', e.target.value)}
                                      fullWidth
                                      InputLabelProps={{ shrink: true }}
                                      inputProps={{ step: 300 }}
                                      error={!!timeSlotErrors[`${hour.day_of_week}-${slotIndex}`]}
                                      InputProps={{
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <IconButton
                                              onClick={() => handleTimeChange(hour.day_of_week, slotIndex, 'start_time', true)}
                                              size="small"
                                            >
                                              <KeyboardArrowUpIcon />
                                            </IconButton>
                                            <IconButton
                                              onClick={() => handleTimeChange(hour.day_of_week, slotIndex, 'start_time', false)}
                                              size="small"
                                            >
                                              <KeyboardArrowDownIcon />
                                            </IconButton>
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={5}>
                                    <TextField
                                      label="Closing Time"
                                      type="time"
                                      value={slot.end_time || ''}
                                      onChange={(e) => handleOperatingHoursChange(hour.day_of_week, slotIndex, 'end_time', e.target.value)}
                                      fullWidth
                                      InputLabelProps={{ shrink: true }}
                                      inputProps={{ step: 300 }}
                                      error={!!timeSlotErrors[`${hour.day_of_week}-${slotIndex}`]}
                                      InputProps={{
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <IconButton
                                              onClick={() => handleTimeChange(hour.day_of_week, slotIndex, 'end_time', true)}
                                              size="small"
                                            >
                                              <KeyboardArrowUpIcon />
                                            </IconButton>
                                            <IconButton
                                              onClick={() => handleTimeChange(hour.day_of_week, slotIndex, 'end_time', false)}
                                              size="small"
                                            >
                                              <KeyboardArrowDownIcon />
                                            </IconButton>
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={2}>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      {slotIndex === 0 && (
                                        <>
                                          <Tooltip title="Copy to other days">
                                            <IconButton
                                              onClick={() => handleCopySettings(hour.day_of_week)}
                                              color="primary"
                                              disabled={!slot.start_time || !slot.end_time}
                                            >
                                              <ContentCopyIcon />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Add another time slot">
                                            <IconButton
                                              onClick={() => handleAddTimeSlot(hour.day_of_week)}
                                              color="primary"
                                            >
                                              <AddIcon />
                                            </IconButton>
                                          </Tooltip>
                                        </>
                                      )}
                                      <Tooltip title="Delete time slot">
                                        <IconButton
                                          onClick={() => handleDeleteTimeSlot(hour.day_of_week, slotIndex)}
                                          color="error"
                                        >
                                          <DeleteIcon />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Grid>
                                </Grid>
                                {slotIndex < hour.timeSlots.length - 1 && (
                                  <Divider sx={{ my: 2 }} />
                                )}
                              </Box>
                            ))}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Copy Settings Dialog */}
          <Dialog open={copyDialogOpen} onClose={() => setCopyDialogOpen(false)}>
            <DialogTitle>Copy Hours to Other Days</DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Select the days you want to copy the hours from {sourceDay ? sourceDay.charAt(0).toUpperCase() + sourceDay.slice(1) : ''} to:
              </Typography>
              <FormGroup>
                {operatingHours
                  .filter(hour => hour.day_of_week !== sourceDay)
                  .map(hour => (
                    <FormControlLabel
                      key={hour.day_of_week}
                      control={
                        <Checkbox
                          checked={selectedDays.includes(hour.day_of_week)}
                          onChange={() => handleDaySelect(hour.day_of_week)}
                        />
                      }
                      label={hour.day_of_week.charAt(0).toUpperCase() + hour.day_of_week.slice(1)}
                    />
                  ))}
              </FormGroup>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCopyDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleCopyConfirm}
                variant="contained"
                disabled={selectedDays.length === 0}
              >
                Copy Hours
              </Button>
            </DialogActions>
          </Dialog>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSaveOperatingHours}
              size="large"
              startIcon={<AccessTimeIcon />}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.text.primary,
                ':hover': { backgroundColor: theme.palette.primary.dark },
                minWidth: 200,
              }}
            >
              Save Operating Hours
            </Button>
          </Box>
        </>
      )}

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CampaignOperatingHours; 